import React, { PropsWithChildren } from 'react';
import styled from 'styled-components';
import { CheckIcon } from '../../assets';
import { theme } from '../../theme';
import { BodyText, CombinedLinkTextProps, LinkTextStyles } from '../typography';

const StepMarkerContainer = styled.li`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: center;
  list-style: none;

  &:first-child {
    align-items: flex-start;
  }
  &:last-child {
    align-items: flex-end;
  }

  * + * {
    margin-top: ${theme.spacings[2].rem};
  }
`;

type StyledStepMarkerProps = {
  isActive: boolean;
};

const StyledStepMarker = styled.span<StyledStepMarkerProps>`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 24px;
  height: 24px;
  border-radius: 12px;
  background-color: ${({ isActive }) =>
    isActive
      ? theme.colors.system.systemGreen
      : theme.colors.neutrals.solidGrays[6]};
  padding: 0;
  margin: 0;
  transition: background-color 200ms ease-in;
  cursor: pointer;

  ${({ isActive }) => !isActive && 'pointer-events: none'};
`;

type StepMarkerLabelProps = CombinedLinkTextProps &
  Pick<ProgressStepMarkerProps, 'isCurrent' | 'isActive'>;

const StepMarkerLabel = styled(BodyText)<StepMarkerLabelProps>`
  a {
    display: flex;
    ${LinkTextStyles};

    ${({ isCurrent }) =>
      isCurrent &&
      `
        color: ${theme.colors.primary.graysGray};
        font-weight: bold;
    `};

    ${({ isActive }) =>
      !isActive &&
      `
        color: ${theme.colors.neutrals.transparentGrays[2]};
        pointer-events: none;
    `};
  }
`;

export type ProgressStepMarkerProps = PropsWithChildren<{
  isActive?: boolean;
  isSuccess?: boolean;
  isCurrent?: boolean;
  onClick?: VoidFunction;
  stepTitle: string;
}>;

/**
 * To be used as a direct child of the Progress component.
 * Can contain an anchor element which if present will be
 * styled correctly based on the currently active step.
 */
export const ProgressStepMarker: React.FC<ProgressStepMarkerProps> = ({
  children,
  isActive = false,
  isSuccess = false,
  isCurrent = false,
  onClick,
}) => {
  return (
    <StepMarkerContainer>
      <StyledStepMarker onClick={onClick} isActive={isActive}>
        {isSuccess && (
          <CheckIcon height={16} width={16} fill={theme.colors.primary.white} />
        )}
      </StyledStepMarker>
      <StepMarkerLabel
        size="md"
        context="secondary"
        isUnderlined={false}
        isCurrent={isCurrent}
        isActive={isActive}
      >
        {children}
      </StepMarkerLabel>
    </StepMarkerContainer>
  );
};
